import { Subject } from 'rxjs';
import createDropdownMenu from '@components/dropdownMenu';
import generateGUID from 'utils/guidGenerator';
import HousingType from 'enums/housingType';

// `key` is what's emitted from getObservable()/read by setValue() — a HousingType key, or `null`
// for "no filter" (the default, "All"; HousingType has no member for "no restriction", since the
// absence of a filter already means exactly that). `label` is the option's visible text, used
// both inside the open list and in the field once selected — there's no separate short/long form
// here, unlike Move-in date's list vs. collapsed labels.
export const HOUSING_TYPE_OPTIONS = [
  { key: null, label: 'All' },
  { key: HousingType.SingleFamily.toString(), label: 'Single family' },
  { key: HousingType.Custom.toString(), label: 'Custom build homes' },
  { key: HousingType.Luxury.toString(), label: 'Luxury homes' },
  { key: HousingType.CondosAndTownHomes.toString(), label: 'Condos & townhomes' },
  { key: HousingType.ModelHomes.toString(), label: 'Model homes' },
];
const DEFAULT_OPTION = HOUSING_TYPE_OPTIONS[0];
const OPTION_BY_KEY = new Map(HOUSING_TYPE_OPTIONS.map((option) => [option.key, option]));

/**
 * Housing type filter — single-select dropdown (Figma "Housing type" field), built on the
 * generic `@components/dropdownMenu`. "All" is the pre-applied default (Confluence).
 *
 * Emits a `HousingType` key (or `null` for "All") via `getObservable()`/`setValue()` — the same
 * vocabulary a consuming feature's own filtering already speaks, so it needs no translation layer
 * of its own.
 *
 * Only produces markup (`getHtml`) and binds behavior to the element the calling control created
 * from it (`new HousingTypeFilter(element)`); it never creates DOM itself.
 */
export default class HousingTypeFilter {
  static getSelector() {
    return '[data-housing-type-filter]';
  }

  static getHtml() {
    const labelId = `housing-type-label-${generateGUID()}`;
    const buttonId = `housing-type-button-${generateGUID()}`;
    // A DOM attribute can't hold a real `null` — an empty string stands in for the default
    // option's `key: null` here, and is converted back in the constructor's subscription.
    const optionsHtml = HOUSING_TYPE_OPTIONS.map(({ key, label }) => `
      <li>
        <button type="button"
                class="nhs-c-dropdown__option nhs-c-dropdown__option--button"
                data-dropdown-option
                data-value="${key ?? ''}">
          ${label}
        </button>
      </li>
    `).join('');

    return `
      <div class="nhs-c-input__wrapper nhs-c-input__wrapper--filter" data-housing-type-filter>
        <p class="nhs-c-input__label" id="${labelId}">Housing type</p>
        <button type="button"
                id="${buttonId}"
                aria-labelledby="${labelId} ${buttonId}"
                class="nhs-c-select-button nhs-c-select-button--modal"
                data-dropdown-toggle
                data-qa="housing_type_filter">
          <span data-value-display>${DEFAULT_OPTION.label}</span>
        </button>
        <ul class="nhs-c-dropdown__list nhs-c-dropdown__list--anchored" data-dropdown-list hidden>
          ${optionsHtml}
        </ul>
      </div>
    `;
  }

  constructor(element) {
    this.element = element;
    this.key = DEFAULT_OPTION.key;
    this.valueDisplay = element.querySelector('[data-value-display]');
    this.menu = createDropdownMenu(element);
    this.value$ = new Subject();
    this.subscription = this.menu.getObservable().subscribe((domValue) => {
      const key = domValue || null;
      this.setValue(key);
      this.value$.next(key);
    });
  }

  getObservable() {
    return this.value$.asObservable();
  }

  // Syncs the field to an externally-known value (e.g. the store's current filter, read back
  // when the modal reopens) without emitting — this reflects state that already exists, it isn't
  // a new user selection.
  setValue(key) {
    this.key = key;
    this.valueDisplay.textContent = (OPTION_BY_KEY.get(key) ?? DEFAULT_OPTION).label;
  }

  destroy() {
    this.subscription.unsubscribe();
    this.menu.destroy();
  }
}
